import * as React from "react";
import "../../assets/crudStyles.css";
import "./styles.css";
import { TextInputCounter } from "../../components/TextInputCounter/TextInputCounter";
import {
  required,
  ImageInput,
  Toolbar,
  SaveButton,
  useNotify,
  ImageField,
  useRecordContext,
  useEditController,
  maxLength,
  TabbedForm,
  FormTab,
} from "react-admin";
import useFileUpload from "../../hooks/useFileUpload";
import { ResourceEditor } from "../../components/ResourceEditor/ResourceEditor";

import { CreateScreen as GivingBackCreate } from "../../components/giving-back-page-sections/create";
import { EditScreen as GivingBackSectionEdit } from "../../components/giving-back-page-sections/edit";
import { RaEditWrapper } from "../../components/RaEditWrapper";

interface IEdit {
  [key: string]: any;
}

const EditToolbar = ({ files, ...props }: any) => {
  return (
    <Toolbar {...props}>
      <SaveButton
        transform={({ ...data }: IEdit) => {
          const mappedRequest: any = {
            ...data,
          };

          const resourceFilesReq = {
            topSectionImageUploadId:
              files.topSectionImageUpload ?? data.topSectionImageUpload.id,
          };

          // Clean up fields.
          delete mappedRequest.topSectionImageUpload;
          delete mappedRequest.order;
          delete mappedRequest.id;
          delete mappedRequest.enabled;

          return {
            ...mappedRequest,
            ...resourceFilesReq,
          };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const EditScreen = (props: any) => {
  const notify = useNotify();

  // Upload Files
  const [resourceFiles, setResourceFiles] = React.useState<{
    topSectionImageUpload: null | any;
  }>({
    topSectionImageUpload: null,
  });

  const [resourceImgs, setResourceImgs] = React.useState<{
    [key: string]: any;
  }>({});

  const renderImgPreview = (name: string) => {
    if (!resourceImgs[name]) {
      return <ImageField source="uri" title="info.original_filename" />;
    }

    return (
      <FileFieldWithContext
        placeholder={
          <div className="MuiBox-root css-cb34tx-RaImageField-root">
            <img
              src={resourceImgs[name].uri}
              title={resourceImgs[name].title}
              width={200}
              height={200}
              className="RaImageField-image"
            />
          </div>
        }
      >
        <ImageField source="uri" title="title" />
      </FileFieldWithContext>
    );
  };

  // Used to track the uploaded files and show in progress or error.
  const [uploadedFileNames, setUploadFileNames] = React.useState<{
    [key: string]: string | boolean;
  }>({});

  const { uploadFile } = useFileUpload();
  const handlePrefileUpload = async (name: string, files: File[]) => {
    const fileName = files[0].name;
    notify(`Uploading file... ${fileName}`);

    setUploadFileNames({ ...uploadedFileNames, [fileName]: "Uploading..." });

    const asset = await uploadFile(files[0], name);
    if (asset !== undefined) {
      setResourceImgs({
        ...resourceImgs,
        [name]: {
          title: asset.info.original_filename,
          uri: asset.uri,
          uploading: false,
        },
      });

      const newValue = asset.id;
      setResourceFiles({ ...resourceFiles, [name]: newValue });
      notify(`Upload completed! File: ${fileName}`);
      setUploadFileNames({ ...uploadedFileNames, [fileName]: true });
    } else {
      notify(`Error uploading file! ${fileName}`);
      setUploadFileNames({
        ...uploadedFileNames,
        [fileName]: "Error uploading file, remove and try again.",
      });
    }
  };

  const FileFieldWithContext = (props: any) => {
    const { title, uri } = useRecordContext();

    if (uri) {
      return props.children;
    }

    if (!Object.hasOwn(uploadedFileNames, title)) {
      return null;
    }

    if (uploadedFileNames[title] === true) {
      return props.placeholder;
    } else {
      return <p>{uploadedFileNames[title]}</p>;
    }
  };

  // Record handler.
  const { record } = useEditController(props);

  if (record) {
    record.enabled = record.publishedAt !== null ? true : false;
  }

  return (
    <RaEditWrapper mutationMode="pessimistic">
      <TabbedForm
        toolbar={<EditToolbar files={resourceFiles} record={record} />}
      >
        <FormTab label="Top">
          <div className="form-text-input-row">
            <TextInputCounter
              formClassName="form-text-input-field"
              source="topSectionTitle"
              validate={[required(), maxLength(100)]}
              limit={100}
            />
          </div>

          <TextInputCounter
            source="topSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSectionImageUpload"
              label="Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSectionImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSectionImageUpload")}
            </ImageInput>
          </div>
        </FormTab>

        <FormTab label="Partner Stories">
          <div className="form-text-input-row">
            <TextInputCounter
              formClassName="form-text-input-field"
              source="partnerStoriesSectionTitle"
              validate={[required(), maxLength(100)]}
              limit={100}
            />
          </div>

          <TextInputCounter
            source="partnerStoriesSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />
        </FormTab>

        <FormTab label="Travel Sustainability">
          <div className="form-text-input-row">
            <TextInputCounter
              formClassName="form-text-input-field"
              source="travelSustainabilitySectionTitle"
              validate={[required(), maxLength(100)]}
              limit={100}
            />
          </div>

          <TextInputCounter
            source="travelSustainabilitySectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />
        </FormTab>

        <FormTab label="Sections">
          <ResourceEditor
            resourceId={"giving-back-page-sections"}
            createNewLabel="Add New Section"
            updatedOrderMsg="Section order updated."
            updatingOrderMsg="Updating section order..."
            heading="Giving Back Sections"
            CreateScreen={GivingBackCreate}
            EditScreen={GivingBackSectionEdit}
            hasOrder={false}
            hasEnabled={false}
          />
        </FormTab>
      </TabbedForm>
    </RaEditWrapper>
  );
};
